import { createApi } from "@reduxjs/toolkit/query/react";
import { useDispatch } from "react-redux";

import {
    ROUTE_GET_LENDING_VAULT_ACTIVITY,
    ROUTE_GET_LENDING_VAULT_ALLOCATIONS,
    ROUTE_GET_LENDING_VAULTS,
    ROUTE_GET_LENDING_VAULTS_HISTORY,
    ROUTE_GET_LENDING_VAULTS_UPDATE_HISTORY
} from "../constants";
import { abfSerializeQueryArgs, marketsPublicBaseQuery } from "./util";
import {
    FetchLendVaultsHistoryParams,
    FetchLendVaultsInfoParams,
    LendVaultActivityResponse,
    LendingVaultAllocation,
    LendingVaultsHistory,
    LendingVaultsInfoResponse
} from "../types";
import { parseLendingVaultInfoResponseFromApi, parseLendVaultActivityResponseFromApi } from "../types/parsers";
import { LendingVaultInfoResponseApi, LendVaultActivityRequestApi, LendVaultActivityResponseApi } from "../types/api";

const LENDING_PUBLIC_VAULT_TAG = "LendingVault";

export const lendingVaultsPublicApi = createApi({
    reducerPath: "abfLendingVaultPublicApi",
    baseQuery: marketsPublicBaseQuery,
    serializeQueryArgs: abfSerializeQueryArgs,
    tagTypes: [LENDING_PUBLIC_VAULT_TAG],
    endpoints: (builder) => ({
        lendingVaultInfos: builder.query<LendingVaultsInfoResponse, FetchLendVaultsInfoParams>({
            providesTags: [LENDING_PUBLIC_VAULT_TAG],
            query(params) {
                return {
                    url: ROUTE_GET_LENDING_VAULTS,
                    method: "POST",
                    body: params
                };
            },
            transformResponse: (response: LendingVaultInfoResponseApi): LendingVaultsInfoResponse => {
                return parseLendingVaultInfoResponseFromApi(response);
            }
        }),
        lendingVaultHistory: builder.query<LendingVaultsHistory[], FetchLendVaultsHistoryParams>({
            providesTags: [LENDING_PUBLIC_VAULT_TAG],
            query(params) {
                return {
                    url: ROUTE_GET_LENDING_VAULTS_HISTORY,
                    method: "POST",
                    body: params
                };
            }
        }),
        lendingVaultUpdateHistory: builder.query<string, void>({
            providesTags: [LENDING_PUBLIC_VAULT_TAG],
            query() {
                return {
                    url: ROUTE_GET_LENDING_VAULTS_UPDATE_HISTORY,
                    method: "POST"
                };
            }
        }),
        lendingVaultAllocations: builder.query<LendingVaultAllocation[], { vaultAddress: string }>({
            providesTags: [LENDING_PUBLIC_VAULT_TAG],
            query(params) {
                return {
                    url: `${ROUTE_GET_LENDING_VAULT_ALLOCATIONS}/${params.vaultAddress}`,
                    method: "GET"
                };
            }
        }),
        lendVaultActivity: builder.query<LendVaultActivityResponse, LendVaultActivityRequestApi>({
            providesTags: [LENDING_PUBLIC_VAULT_TAG],
            query(body) {
                return {
                    url: ROUTE_GET_LENDING_VAULT_ACTIVITY,
                    method: "POST",
                    body
                };
            },
            transformResponse: (response: LendVaultActivityResponseApi): LendVaultActivityResponse =>
                parseLendVaultActivityResponseFromApi(response)
        })
    })
});

export const {
    useLendingVaultInfosQuery,
    useLendingVaultHistoryQuery,
    useLendingVaultAllocationsQuery,
    useLendVaultActivityQuery
} = lendingVaultsPublicApi;

export function useLendingVaultsPublicApi() {
    const dispatch = useDispatch();

    return {
        resetLendingVaultPublicApi: () =>
            dispatch(lendingVaultsPublicApi.util.invalidateTags([LENDING_PUBLIC_VAULT_TAG]))
    };
}
